<div class="surface-section min-h-screen bg-gray-50">
    <!-- Header -->
    <div
        class="flex flex-column lg:flex-row lg:justify-content-between lg:align-items-start p-4 bg-white border-bottom-1 surface-border">
        <div class="flex align-items-start gap-3">
            <button pButton icon="pi pi-arrow-left" (click)="goBack()"
                class="p-button-text p-button-rounded p-button-lg" [disabled]="loading">
            </button>
            <div class="flex-1">
                <div class="flex align-items-center gap-3 mb-2">
                    <h1 class="text-2xl font-bold m-0 text-900" *ngIf="!loading">
                        {{ sequence?.name || 'Email Sequence' }}
                    </h1>
                    <p-skeleton *ngIf="loading" width="250px" height="2rem"></p-skeleton>
                </div>
                <div *ngIf="!loading" class="text-600 mb-2">
                    <p class="m-0 mb-1">{{ sequence?.description || 'No description available' }}</p>
                    <p class="m-0 text-sm text-primary">Platform: {{ sequence?.provider | titlecase }}</p>
                    <app-strategy-route-chip [companyId]="sequence?.companyId" [providerCampaignId]="sequence?.providerCampaignId"></app-strategy-route-chip>
                </div>
                <div *ngIf="loading">
                    <p-skeleton width="280px" height="1rem" class="mb-1"></p-skeleton>
                    <p-skeleton width="120px" height="1rem"></p-skeleton>
                </div>
            </div>
        </div>
        <div class="flex align-items-center gap-2 mt-3 lg:mt-0" *ngIf="!loading && sequence">
            <p-tag [value]="capitalizeFirst(sequence.status)" [severity]="getStatusSeverity()"
                styleClass="text-sm font-medium">
            </p-tag>
            <button pButton [icon]="getActionButtonConfig().icon" [label]="getActionButtonConfig().label" 
                class="p-button-outlined p-button-sm" (click)="getActionButtonConfig().action()">
            </button>
        </div>
        <div *ngIf="loading" class="flex gap-2 mt-3 lg:mt-0">
            <p-skeleton width="60px" height="1.5rem"></p-skeleton>
            <p-skeleton width="80px" height="2rem"></p-skeleton>
        </div>
    </div>

    <div class="p-4">
        <!-- Key Performance Indicators -->
        <div class="grid mb-4" *ngIf="!loading && sequence">
            <!-- Total Sent -->
            <div class="col-12 md:col-3">
                <div class="bg-white border-round p-4 border-1 surface-border h-full">
                    <div class="flex align-items-center">
                        <div
                            class="bg-blue-50 border-circle w-3rem h-3rem flex align-items-center justify-content-center mr-3">
                            <i class="pi pi-send text-blue-500 text-xl"></i>
                        </div>
                        <div>
                            <p class="text-600 text-sm m-0 mb-1">Emails Sent</p>
                            <p class="text-4xl font-bold text-900 m-0">{{ formatNumber(sequence?.metrics?.emailsSentCount || 0) }}</p>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Opens -->
            <div class="col-12 md:col-3">
                <div class="bg-white border-round p-4 border-1 surface-border h-full">
                    <div class="flex align-items-center">
                        <div
                            class="bg-green-50 border-circle w-3rem h-3rem flex align-items-center justify-content-center mr-3">
                            <i class="pi pi-eye text-green-500 text-xl"></i>
                        </div>
                        <div>
                            <p class="text-600 text-sm m-0 mb-1">Opens</p>
                            <p class="text-4xl font-bold text-900 m-0">{{ formatNumber(sequence?.metrics?.openCount || 0) }}</p>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Replies -->
            <div class="col-12 md:col-3">
                <div class="bg-white border-round p-4 border-1 surface-border h-full">
                    <div class="flex align-items-center">
                        <div
                            class="bg-purple-50 border-circle w-3rem h-3rem flex align-items-center justify-content-center mr-3">
                            <i class="pi pi-reply text-purple-500 text-xl"></i>
                        </div>
                        <div>
                            <p class="text-600 text-sm m-0 mb-1">Replies</p>
                            <p class="text-4xl font-bold text-900 m-0">{{ formatNumber(sequence?.metrics?.replyCount || 0) }}</p>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Meetings -->
            <div class="col-12 md:col-3">
                <div class="bg-white border-round p-4 border-1 surface-border h-full">
                    <div class="flex align-items-center">
                        <div
                            class="bg-orange-50 border-circle w-3rem h-3rem flex align-items-center justify-content-center mr-3">
                            <i class="pi pi-calendar text-orange-500 text-xl"></i>
                        </div>
                        <div>
                            <p class="text-600 text-sm m-0 mb-1">Meetings</p>
                            <p class="text-4xl font-bold text-900 m-0">{{ formatNumber(sequence?.metrics?.meetings || 0) }}</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Loading state for KPIs -->
        <div class="grid mb-4" *ngIf="loading">
            <div class="col-12 md:col-3" *ngFor="let item of [1,2,3,4]">
                <div class="bg-white border-round p-4 border-1 surface-border h-full">
                    <div class="flex align-items-center">
                        <p-skeleton shape="circle" size="3rem" class="mr-3"></p-skeleton>
                        <div class="flex-1">
                            <p-skeleton width="60px" height="1rem" class="mb-2"></p-skeleton>
                            <p-skeleton width="40px" height="2.5rem"></p-skeleton>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Sequence Steps Section -->
        <div class="bg-white border-round p-4 border-1 surface-border" *ngIf="!loading && steps.length > 0">
            <div class="flex align-items-center mb-4">
                <i class="pi pi-list text-900 text-xl mr-2"></i>
                <h3 class="text-xl font-bold text-900 m-0">Sequence Steps ({{ steps.length }})</h3>
            </div>

            <!-- Step Items -->
            <div class="sequence-steps">
                <div *ngFor="let step of steps; let last = last" class="step-item">
                    <!-- Step Content -->
                    <div class="flex align-items-start gap-4 mb-4">
                        <!-- Step Number -->
                        <div
                            class="step-number bg-blue-100 border-circle w-2rem h-2rem flex align-items-center justify-content-center flex-shrink-0">
                            <span class="text-blue-700 font-bold text-sm">{{ step.stepOrder }}</span>
                        </div>

                        <!-- Step Details -->
                        <div class="flex-1">
                            <div class="bg-gray-50 border-round p-4 mb-3">
                                <!-- Step Header -->
                                <div class="flex align-items-center gap-2 mb-2">
                                    <i class="pi" [ngClass]="getStepIcon(step)" class="text-900"></i>
                                    <h4 class="text-lg font-bold text-900 m-0">{{ getStepTitle(step) }}</h4>
                                    <p-chip *ngIf="step.abTestingEnabled" label="A/B Test"
                                        styleClass="p-chip-sm bg-blue-100 text-blue-700">
                                    </p-chip>
                                </div>
                                
                                <!-- Template Info -->
                                <div class="mb-2">
                                    <p class="text-600 text-sm m-0 mb-1"><strong>Template:</strong> {{ step.templateName || 'No template' }}</p>
                                    <p class="text-600 text-sm m-0 mb-1" *ngIf="step.templateSubject"><strong>Subject:</strong> {{ step.templateSubject }}</p>
                                </div>

                                <!-- Wait Time -->
                                <div class="flex align-items-center text-600 text-sm mb-2">
                                    <i class="pi pi-clock mr-1"></i>
                                    <span>{{ getWaitText(step) }}</span>
                                </div>

                                <!-- A/B Test Configuration -->
                                <div *ngIf="step.abTestingEnabled" class="mt-3 p-3 bg-blue-50 border-round">
                                    <div class="flex align-items-center gap-2 mb-2">
                                        <i class="pi pi-flask text-blue-600"></i>
                                        <span class="font-semibold text-blue-900">A/B Test Configuration</span>
                                    </div>
                                    <div *ngIf="step.abVariantSubject" class="mb-2">
                                        <p class="text-blue-700 text-sm m-0"><strong>Variant Subject:</strong> {{ step.abVariantSubject }}</p>
                                    </div>
                                    <div *ngIf="step.abVariantBody" class="mb-2">
                                        <p class="text-blue-700 text-sm m-0"><strong>Variant Body:</strong> {{ step.abVariantBody }}</p>
                                    </div>
                                    <div *ngIf="step.abVariantCTA">
                                        <p class="text-blue-700 text-sm m-0"><strong>Variant CTA:</strong> {{ step.abVariantCTA }}</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Connector Line (not for last item) -->
                    <div *ngIf="!last" class="step-connector ml-1 mb-3">
                        <div class="connector-line bg-surface-border"></div>
                    </div>
                </div>
            </div>
        </div>

        <!-- No Steps Message -->
        <div class="bg-white border-round p-4 border-1 surface-border text-center" *ngIf="!loading && steps.length === 0">
            <i class="pi pi-inbox text-4xl text-400 mb-3"></i>
            <h3 class="text-xl text-600 mb-2">No Steps Found</h3>
            <p class="text-600">This sequence doesn't have any configured steps yet.</p>
        </div>

        <!-- Loading state for sequence steps -->
        <div class="bg-white border-round p-4 border-1 surface-border" *ngIf="loading">
            <p-skeleton width="200px" height="1.5rem" class="mb-4"></p-skeleton>
            <div *ngFor="let item of [1,2,3]" class="mb-4">
                <div class="flex align-items-start gap-4">
                    <p-skeleton shape="circle" size="2rem"></p-skeleton>
                    <div class="flex-1">
                        <div class="bg-gray-50 border-round p-4">
                            <p-skeleton width="150px" height="1.2rem" class="mb-2"></p-skeleton>
                            <p-skeleton width="120px" height="1rem" class="mb-2"></p-skeleton>
                            <p-skeleton width="100px" height="1rem"></p-skeleton>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</div>